import React, {useEffect, useState} from 'react';
import {Formik} from 'formik';
import {useRouteMatch} from 'react-router-dom';
import Form_12View from './Form_12-view';
import {checkRequiredValues} from '../../../../common/utils/form/checkRequiredValues';
import {normalizeFormValues} from '../../../../common/utils/form/normalizeFormValues';
import {pathnames as P} from '../../../../common/constants/pathnames';
import SilentSubmitHandler from '../SilentSubmitHandler';
import ModalPreviewDocument from '../../../../common/components/ModalPreviewDocument';
import {
  loanToPropertyBondOptions,
  buildingMatchSurroundingsOptions,
  propertyLoanLegalRelationOptions,
  propertyIdentifiedByOptions,
} from '../../../../common/constants/options';
import {isFileUploaded} from '../../utils/isFileUploaded';
import {fileTypeId as T} from '../../constants/fileTypeId';
import {uploadingProgress} from '../../../../common/utils/form/uploadingProgress';
import ButtonSubmitForm from '../ButtonSubmitForm';
import SpinnerContainer from '../../../../common/components/SpinnerContainer';
import {
  filterFileListByFileTypeId,
  getSharedAndPrivateFiles,
  getFileFolderID
} from '../../utils/fileHelpers';
import {getOption} from "../../utils/getOption";

const fileTypeIds = {
  buildCostEstimate: T.BUILD_COST_ESTIMATE,
  renovationCostEstimate: T.RENOVATION_COST_ESTIMATE,
};

const Form_12 = ({
                   submitStep,
                   stepData,
                   getStepData,
                   uploadFile,
                   getShumaStepStatus,
                   showGlobalErrorMessage,
                   shumaTypeID,
                   getFileList,
                   filesListData
                 }) => {
  const [reinit, setReinit] = useState(true);
  const [progress, setProgress] = useState(0);
  const [showUploadingFilePopUp, setShowUploadingFilePopUp] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    getFileList({shumaId})
  }, []);

  const {
    fileList,
    finalMarketPrice,
    propertyBuildCostEstimate,
    quickRealizeDeduction,
    realizeCost,
    improvementLevy,
    bettermentTax,
    ramiCost,
    loanToPropertyBond,
    buildingMatchSurroundings,
    loanToPropertyBondText,
    buildingMatchSurroundingsText,
    propertyArnonaArea,
    mortgageEntityEmphasis,
    propertyLoanLegalRelation,
    propertyIdentifiedBy,
    propertyLandRemainingValueEstimate,
    deductionAmount,
    ...rest
  } = stepData;

  const propertyIdentifiedByArray = propertyIdentifiedBy ? propertyIdentifiedBy.split(',') : [];
  const previousPropertyIdentifiedBy = propertyIdentifiedBy ? propertyIdentifiedByOptions.filter(opt => propertyIdentifiedByArray.includes(`${opt.value}`)) : [];

  const initialValues = {
    estimatePriceFinal: '',
    finalMarketPrice: finalMarketPrice ? finalMarketPrice.toFixed(2) : 0,
    propertyBuildCostEstimate: propertyBuildCostEstimate ? propertyBuildCostEstimate.toFixed(2) : 0,
    propertyLandRemainingValueEstimate: propertyLandRemainingValueEstimate
      ? propertyLandRemainingValueEstimate.toFixed(2) : 0,
    quickRealizeDeduction: (typeof quickRealizeDeduction === 'number') ? quickRealizeDeduction.toFixed(2) : null,
    realizeCost: (typeof realizeCost === 'number') ? realizeCost.toFixed(2) : null,
    improvementLevy: improvementLevy ? improvementLevy.toFixed(2) : 0,
    bettermentTax: bettermentTax ? bettermentTax.toFixed(2) : 0,
    ramiCost: ramiCost ? ramiCost.toFixed(2) : 0,
    calculationNotes: '',
    buildingMatchSurroundings: getOption(buildingMatchSurroundingsOptions, buildingMatchSurroundings),
    loanToPropertyBond: getOption(loanToPropertyBondOptions, loanToPropertyBond),
    loanToPropertyBondText: loanToPropertyBondText || '',
    buildingMatchSurroundingsText: buildingMatchSurroundingsText || '',
    propertyArnonaArea: propertyArnonaArea || '',
    mortgageEntityEmphasis: mortgageEntityEmphasis || '',
    propertyLoanLegalRelation: getOption(propertyLoanLegalRelationOptions, propertyLoanLegalRelation),
    propertyIdentifiedBy: propertyIdentifiedBy ? previousPropertyIdentifiedBy : null,
    propertyIdentifiedByText: '',
    propertyLoanLegalRelationText: '',
    deductionAmount: deductionAmount || '',
    taxAndCostDesc: '',
    selfBuildDesc: '',
    renovationDetailsDesc: '',
    renovationCostDesc: '',

    fileType34Switch: false,
    fileType35Switch: false,

    ...rest
  };
  const {params: {shumaId, formId: stepId}} = useRouteMatch(P.INSPECTION_AND_FORM_IDS);

  const validate = values => {
    const {
      finalMarketPrice,
      propertyBuildCostEstimate,
      propertyLandRemainingValueEstimate,
      quickRealizeDeduction,
      loanToPropertyBond,
      buildingMatchSurroundings,
      loanToPropertyBondText,
    } = values;

    switch (shumaTypeID) {
      case 1:
        return null
      case 2:
        return checkRequiredValues({
          finalMarketPrice,
          propertyBuildCostEstimate,
          propertyLandRemainingValueEstimate,
          quickRealizeDeduction,
          loanToPropertyBond,
          buildingMatchSurroundings,
          loanToPropertyBondText,
        }, {});
      case 3:
      case 4:
        return checkRequiredValues({
          finalMarketPrice,
          quickRealizeDeduction,
          loanToPropertyBond,
          buildingMatchSurroundings,
          loanToPropertyBondText,
        }, {});
      default:
        return null
    }
  };

  const changedValue = (value) => {
    if (value) {
      return value.map(item => item.value).join()
    }
  }

  const submitWithoutFormReinitialization = values => {
    const {
      finalMarketPrice,
      propertyBuildCostEstimate,
      propertyLandRemainingValueEstimate,
      quickRealizeDeduction,
      realizeCost,
      improvementLevy,
      bettermentTax,
      ramiCost,
      calculationNotes,
      loanToPropertyBond,
      buildingMatchSurroundings,
      loanToPropertyBondText,
      buildingMatchSurroundingsText,
      propertyArnonaArea,
      mortgageEntityEmphasis,
      propertyLoanLegalRelation,
      propertyLoanLegalRelationText,
      propertyIdentifiedBy,
      propertyIdentifiedByText,
      deductionAmount,
      taxAndCostDesc,
      selfBuildDesc,
      renovationDetailsDesc,
      renovationCostDesc,
    } = normalizeFormValues(values);
    const data = {
      shumaID: shumaId,
      shumaTypeID,
      finalMarketPrice,
      propertyBuildCostEstimate,
      propertyLandRemainingValueEstimate,
      quickRealizeDeduction,
      realizeCost,
      improvementLevy,
      bettermentTax,
      ramiCost,
      calculationNotes,
      loanToPropertyBond,
      buildingMatchSurroundings,
      loanToPropertyBondText,
      buildingMatchSurroundingsText,
      propertyArnonaArea,
      mortgageEntityEmphasis,
      propertyLoanLegalRelation,
      propertyLoanLegalRelationText,
      propertyIdentifiedBy: changedValue(propertyIdentifiedBy),
      propertyIdentifiedByText,
      deductionAmount,
      taxAndCostDesc,
      selfBuildDesc,
      renovationDetailsDesc,
      renovationCostDesc,
    };
    setReinit(false);  //https://github.com/formium/formik/issues/2369
    return submitStep({step: 12, data})
      .then(() => setReinit(true));
  };

  const handleDataSubmit = (values, shumaId, dirty) => {
    if (dirty) {
      submitWithoutFormReinitialization(values)
        .then(() => getShumaStepStatus({shumaId, resetValues: false}))
        .catch((err) => showGlobalErrorMessage(err.message));
    }
  };

  const buildCostEstimateFileUploaded = isFileUploaded({fileList: filesListData, fileTypeID: T.BUILD_COST_ESTIMATE});
  const renovationCostEstimateFileUploaded = isFileUploaded({fileList: filesListData, fileTypeID: T.RENOVATION_COST_ESTIMATE});

  const handleFileUpload = formikProps => fileTypeId => (file, payload) => {
    setShowUploadingFilePopUp(true);
    submitFiles(fileTypeId)(file, payload)
      .then(() => uploadingProgress(setProgress))
      .then(() => submitWithoutFormReinitialization(formikProps.values))
      .then(() => getShumaStepStatus({shumaId, resetValues: false}))
      .finally(() => {
        setShowUploadingFilePopUp(false);
        getFileList({shumaId});
        getStepData({shumaId, stepId});
      })
  };

  const submitFiles = fileTypeId => (fileToSave, payload) => {
    return uploadFile({shumaId, filename: fileToSave.name, fileTypeId, fileToSave}, payload);
  };

  const handleDataSubmitForPreview = (values, shumaId, dirty) => {
    if (dirty) {
      submitWithoutFormReinitialization(values)
        .then(() => getStepData({shumaId, stepId}))
        .then(() => getShumaStepStatus({shumaId, resetValues: false}))
        .catch((err) => showGlobalErrorMessage(err.message));
    }
  };

  const handleDataSubmitForSave = (values, shumaId, dirty) => {
    if (dirty) {
      setLoading(true);
      submitWithoutFormReinitialization(values)
        .then(() => getStepData({shumaId, stepId}))
        .then(() => getShumaStepStatus({shumaId, resetValues: false}))
        .catch((err) => showGlobalErrorMessage(err.message))
        .finally(() => setLoading(false));
    }
  };

  const handleSubmitForSharedFiles = formikProps => {
    Promise.resolve()
      .then(() => {
        submitWithoutFormReinitialization(formikProps.values);
      })
      .then(() => getShumaStepStatus({shumaId, resetValues: false}));
  };

  const getFileSelectOptions = formikProps => {
    const filteredSharedFileID34 = filterFileListByFileTypeId(fileList, fileTypeIds.buildCostEstimate);
    const filteredSharedFileID35 = filterFileListByFileTypeId(fileList, fileTypeIds.renovationCostEstimate);
    const onSubmit = () => handleSubmitForSharedFiles(formikProps);

    return {
      buildCostEstimate: {
        type: getSharedAndPrivateFiles(filteredSharedFileID34),
        fileTypeID: fileTypeIds.buildCostEstimate,
        onSubmit,
        folderID: getFileFolderID(filesListData, fileTypeIds.buildCostEstimate),
      },
      renovationCostEstimate: {
        type: getSharedAndPrivateFiles(filteredSharedFileID35),
        fileTypeID: fileTypeIds.renovationCostEstimate,
        onSubmit,
        folderID: getFileFolderID(filesListData, fileTypeIds.renovationCostEstimate),
      },
    };
  };

  return (
    <Formik
      initialValues={initialValues}
      validate={validate}
      onSubmit={() => {
      }}
      enableReinitialize={reinit}
      validateOnChange={true}
      validateOnBlur={false}
    >
      {(formikProps) => (
        <>
          <SilentSubmitHandler
            onSubmit={handleDataSubmit}
            values={formikProps.values}
            shumaId={shumaId}
            dirty={formikProps.dirty}
          />
          <ButtonSubmitForm
            onClick={() => {
              handleDataSubmitForSave(
                formikProps.values,
                shumaId,
                formikProps.dirty
              )
            }}
            disabled={!formikProps.dirty}
          />
          <ModalPreviewDocument
            submitOnOpen={() => {
              handleDataSubmitForPreview(
                formikProps.values,
                shumaId,
                formikProps.dirty
              )
            }}
          />
          {loading ? <SpinnerContainer/> : (
            <Form_12View
              {...formikProps}
              shumaID={shumaId}
              stepID={stepId}
              shumaTypeID={shumaTypeID}
              prevPagePath={P.GET_FORM_BY_ID_AND_STEP(shumaId, 10)}
              nextPagePath={P.GET_FORM_BY_ID_AND_STEP(shumaId, 8)}
              options={{
                loanToPropertyBondOptions,
                buildingMatchSurroundingsOptions,
                propertyLoanLegalRelationOptions,
                propertyIdentifiedByOptions,
              }}
              onFileSelected={handleFileUpload(formikProps)}
              fileTypeIds={fileTypeIds}
              fileUploaded={{
                buildCostEstimateFileUploaded,
                renovationCostEstimateFileUploaded
              }}
              progress={progress}
              showUploadingFilePopUp={showUploadingFilePopUp}
              fileSelectOptions={getFileSelectOptions(formikProps)}
              filesListData={filesListData ?? []}
            />
          )}
        </>
      )}
    </Formik>
  );
};

export default Form_12;